import { Injectable } from '@angular/core';
import { map, Observable, take } from 'rxjs';

import {
  DprActivitiesGridSync,
  DprActivitiesGridSyncResult,
  DprHseGridSync,
  DprPersonnelGridSync,
  DprWeatherGridSync,
  DprConsumableGridSync,
  DprSummaryGridSyncResult,
  DprSummaryGridSync,
  DprProgressGridSync,
  DprAppendixGridSyncResult,
  DprAppendixGridSync,
  DprForecastActivitiesGridSync,
  DprForecastActivitiesGridSyncResult,
  DprHiddenGridSync,
  DprStatusTrackerGridSync,
  DprProgressWithoutTargetGridSync,
  DprFreeTextFieldSyncResult,
  DprFreeTextFieldSync,
  DprHiddenGridSyncResult,
  DprTableOfColumnGridSync,
} from '@dpr-details/models/sync';
import {
  DprDetails,
  DprWeather,
  DprHse,
  DprConsumable,
  DprPersonnel,
  DprProgress,
  DprStatusTracker,
  DprTableOfColumns,
  DprProgressWithoutTarget,
} from '@shared/models/dpr';
import {
  CopyPreviousDprForecastActivitiesOptions,
  CopyPreviousDprForecastActivitiesResult,
} from '@dpr-details/models';
import { OfflineFacade } from '@shared/state/facades';
import { DprDetailsOfflineService } from '../sync';
import { DprDetailsBasePersistenceService } from '.';

@Injectable()
export class DprDetailsLocalPersistenceService extends DprDetailsBasePersistenceService {
  constructor(
    private offlineFacade: OfflineFacade,
    private dprDetailsOfflineService: DprDetailsOfflineService
  ) {
    super();
  }

  getDprDetails(dprDetailId: number | string): Observable<DprDetails> {
    return this.offlineFacade.getDprDetails(dprDetailId).pipe(take(1));
  }

  syncDprActivities(
    data: DprActivitiesGridSync
  ): Observable<DprActivitiesGridSyncResult> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprActivities(data);
  }

  syncDprStatusTracker(
    data: DprStatusTrackerGridSync
  ): Observable<DprStatusTracker> {
    this.offlineFacade.setSyncOfflineGuid();
    return this.dprDetailsOfflineService.saveDprStatusTracker(data);
  }

  syncDprWeather(data: DprWeatherGridSync): Observable<DprWeather> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprWeather(data);
  }

  syncDprHse(data: DprHseGridSync): Observable<DprHse> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprHse(data);
  }

  syncDprPersonnel(data: DprPersonnelGridSync): Observable<DprPersonnel> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprPersonnel(data);
  }

  syncDprConsumable(data: DprConsumableGridSync): Observable<DprConsumable> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprConsumable(data);
  }

  syncDprSummary(
    data: DprSummaryGridSync
  ): Observable<DprSummaryGridSyncResult> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprSummary(data);
  }

  syncDprForecastActivities(
    data: DprForecastActivitiesGridSync
  ): Observable<DprForecastActivitiesGridSyncResult> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprForecastActivities(data);
  }

  copyDprForecastActivitiesFromPreviousDpr(
    options: CopyPreviousDprForecastActivitiesOptions
  ): Observable<CopyPreviousDprForecastActivitiesResult> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.copyDprForecastActivitiesFromPreviousDpr(
      options
    );
  }

  syncDprSummaryExternalComment(
    data: DprSummaryGridSync
  ): Observable<DprSummaryGridSyncResult> {
    return this.syncDprSummary(data);
  }

  syncDprFreeTextField(
    data: DprFreeTextFieldSync,
    _isAutoSyncFromAdminChange: boolean
  ): Observable<DprFreeTextFieldSyncResult> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprFreeTextFields(data);
  }

  syncDprProgress(data: DprProgressGridSync): Observable<DprProgress> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.syncDprProgress(data);
  }

  syncDprProgressWithoutTarget(
    data: DprProgressWithoutTargetGridSync
  ): Observable<DprProgressWithoutTarget> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.syncDprProgressWithoutTarget(data);
  }

  syncDprTableOfColumns(
    data: DprTableOfColumnGridSync
  ): Observable<DprTableOfColumns> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprTableOfColumns(data);
  }

  syncDprAppendix(
    data: DprAppendixGridSync
  ): Observable<DprAppendixGridSyncResult> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprAppendix(data);
  }

  syncDprHidden(data: DprHiddenGridSync): Observable<DprHiddenGridSyncResult> {
    this.offlineFacade.setSyncOfflineGuid(); // set flag for offline sync operation
    return this.dprDetailsOfflineService.saveDprHidden(data);
  }

  getPreviousDprDetailsId(dprDate: Date): Observable<number | string> {
    return this.dprDetailsOfflineService
      .getPreviousDprDetails(dprDate)
      .pipe(map((dprDetails) => dprDetails?.id));
  }

  getNextDprDetailsId(dprDate: Date): Observable<number | string> {
    return this.offlineFacade.query.combinedDprDetailsList$.pipe(
      take(1),
      map((dprDetailsList: DprDetails[]) => {
        const sortedByDate = dprDetailsList
          .copy()
          .sort(
            (a, b) =>
              new Date(a.dprDateTime).getTime() -
              new Date(b.dprDateTime).getTime()
          );

        const nextDprDetails = sortedByDate.find(
          (x) => new Date(x.dprDateTime).getTime() > new Date(dprDate).getTime()
        );

        return nextDprDetails?.id;
      })
    );
  }

  isFirstDpr(dprDate: Date): Observable<boolean> {
    return this.offlineFacade.query.combinedDprDetailsList$.pipe(
      take(1),
      map((dprDetailsList: DprDetails[]) =>
        dprDetailsList.every(
          (x) =>
            new Date(x.dprDateTime).getTime() >= new Date(dprDate).getTime()
        )
      )
    );
  }
}
